import axios from 'axios';
import { format } from 'date-fns';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { DatePicker } from 'react-datepicker';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  MouseHandlerDataParam,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { useAppDispatch } from 'src/app/hooks';
import { Errors } from 'src/constants/errorConstants';
import { addSnackbar } from 'src/features/globalSlice';
import { SnackbarType } from 'src/store/models/Snackbar';

import Input from '../Input/Input';
import ShadowBox from '../ShadowBox/ShadowBox';
import classes from './HeatingApplianceHistory.module.scss';
import {
  downsampleMultiTemp,
  filterHistory,
  getTodayEnd,
  getTodayStart,
} from './HeatingApplianceHistory.utils';

export interface DeviceData {
  timestamp: string;
  gasTempC: number;
  waterTempC: number;
  fanSpeed: number;
  burningPower: number;
  status: string;
}

const HeatingApplianceHistory: FC = () => {
  const dispatch = useAppDispatch();

  const [history, setHistory] = useState<DeviceData[]>([]);
  const [tempsShown, setTempsShown] = useState<{
    gasTempC: boolean;
    waterTempC: boolean;
  }>({ gasTempC: true, waterTempC: true });
  const [startDate, setStartDate] = useState<Date | null>(getTodayStart());
  const [endDate, setEndDate] = useState<Date | null>(getTodayEnd());
  const [refAreaLeft, setRefAreaLeft] = useState<string | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<string | null>(null);

  useEffect(() => {
    axios
      .get('/json/pellet-boiler-data.json')
      .then((response) => {
        setHistory(response.data);
      })
      .catch(() => {
        dispatch(
          addSnackbar({
            type: SnackbarType.ERROR,
            body: Errors.UNKNOWN_ERROR,
            snackKey: Date.now(),
          })
        );
      });
  }, [dispatch]);

  const handleLegendClick = (data: unknown) => {
    if (
      typeof data === 'object' &&
      data !== null &&
      'dataKey' in data &&
      typeof (data as { dataKey: unknown }).dataKey === 'string'
    ) {
      const dataKey = (data as { dataKey: string }).dataKey;
      if (dataKey === 'gasTempC' || dataKey === 'waterTempC') {
        setTempsShown((prev) => ({
          ...prev,
          [dataKey]: !prev[dataKey as keyof typeof prev],
        }));
      }
    }
  };

  const onChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const shownHistory = useMemo(() => {
    return downsampleMultiTemp(
      filterHistory(history, startDate?.toISOString() || '', endDate?.toISOString() || ''),
      1000
    )?.map((item, i, array) => ({
      ...item,
      offStatus: item.status === 'Off' || (i > 0 && array[i - 1].status === 'Off') ? 1 : null,
      heatingUpStatus:
        item.status === 'Heating up' || (i > 0 && array[i - 1].status === 'Heating up') ? 1 : null,
      burningStatus:
        item.status === 'Burning' || (i > 0 && array[i - 1].status === 'Burning') ? 1 : null,
      coolingDownStatus:
        item.status === 'Cooling down' || (i > 0 && array[i - 1].status === 'Cooling down')
          ? 1
          : null,
    }));
  }, [history, startDate, endDate]);

  const dateFormatType = useMemo(() => {
    if (shownHistory.length === 0) return 'full';

    const dates = shownHistory.map((item) => new Date(item.timestamp));
    const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
    const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));

    const isSameDay =
      minDate.getFullYear() === maxDate.getFullYear() &&
      minDate.getMonth() === maxDate.getMonth() &&
      minDate.getDate() === maxDate.getDate();

    const isSameYear = minDate.getFullYear() === maxDate.getFullYear();

    if (isSameDay) return 'hours';
    if (isSameYear) return 'dayMonth';
    return 'full';
  }, [shownHistory]);

  const formatTimestamp = (value: string): string => {
    if (!value) return '';
    const date = Date.parse(value);
    if (!date) return '';

    const dateObj = new Date(date);

    switch (dateFormatType) {
      case 'hours':
        return format(dateObj, 'HH:mm');
      case 'dayMonth':
        return format(dateObj, 'dd.MM');
      case 'full':
        return format(dateObj, 'dd.MM.yyyy');
      default:
        return format(dateObj, 'dd.MM.yyyy');
    }
  };

  const onMouseDown = useCallback((e: MouseHandlerDataParam) => {
    setRefAreaLeft(e.activeLabel);
    setRefAreaRight(null);
  }, []);

  const onMouseUp = useCallback(
    (e: MouseHandlerDataParam) => {
      if (!refAreaLeft || !e.activeLabel || refAreaLeft === e.activeLabel) return;
      setRefAreaRight(e.activeLabel);
      setTimeout(() => {
        setRefAreaLeft(null);
        setRefAreaRight(null);
        setStartDate(new Date(refAreaLeft));
        setEndDate(new Date(e.activeLabel));
      }, 100);
    },
    [refAreaLeft]
  );

  return (
    <ShadowBox className={classes.Container}>
      <div className={classes.Header}>
        <p>Temperature</p>
        <div>
          <DatePicker
            selected={startDate}
            onChange={onChange}
            startDate={startDate}
            endDate={endDate}
            maxDate={getTodayEnd()}
            selectsRange
            showTimeInput
            dateFormat="dd.MM.yyyy"
            wrapperClassName={classes.DatePicker}
            customInput={<Input InputProps={{ readOnly: true }} />}
          >
            <div>
              <button
                type="button"
                onClick={() => {
                  setStartDate(new Date(getTodayStart().getTime() - 7 * 24 * 60 * 60 * 1000));
                  setEndDate(new Date(getTodayEnd().getTime()));
                }}
              >
                last 7 days
              </button>
              <button
                type="button"
                onClick={() => {
                  setStartDate(new Date(getTodayStart().getTime() - 14 * 24 * 60 * 60 * 1000));
                  setEndDate(new Date(getTodayEnd().getTime()));
                }}
              >
                last 14 days
              </button>
              <button
                type="button"
                onClick={() => {
                  setStartDate(new Date(getTodayStart().getTime() - 30 * 24 * 60 * 60 * 1000));
                  setEndDate(new Date(getTodayEnd().getTime()));
                }}
              >
                last month
              </button>
              <button
                type="button"
                onClick={() => {
                  setStartDate(new Date(getTodayStart().getTime() - 90 * 24 * 60 * 60 * 1000));
                  setEndDate(new Date(getTodayEnd().getTime()));
                }}
              >
                last 3 months
              </button>
              <button
                type="button"
                onClick={() => {
                  setStartDate(new Date(getTodayStart().getTime() - 365 * 24 * 60 * 60 * 1000));
                  setEndDate(new Date(getTodayEnd().getTime()));
                }}
              >
                last 12 months
              </button>
            </div>
          </DatePicker>
        </div>
      </div>
      <ResponsiveContainer
        style={{ minBlockSize: '60px', userSelect: 'none' }}
        width="100%"
        height="100%"
      >
        <AreaChart
          data={shownHistory}
          margin={{
            top: 5,
            right: 0,
            left: 0,
            bottom: 5,
          }}
          style={{ outline: 'none' }}
        >
          <XAxis dataKey="timestamp" tickFormatter={formatTimestamp} />
          <YAxis domain={[0, 1]} style={{ opacity: 0 }} />
          <Tooltip content={(props) => <BarTooltip {...props} />} />
          <Area
            dataKey="offStatus"
            name="Off"
            fill="#808080"
            animationDuration={0}
            stroke="#808080"
          />
          <Area
            dataKey="heatingUpStatus"
            name="Heating up"
            fill="#FFA500"
            animationDuration={0}
            stroke="#FFA500"
          />
          <Area
            dataKey="burningStatus"
            name="Burning"
            fill="#FF4500"
            animationDuration={0}
            stroke="#FF4500"
          />
          <Area
            dataKey="coolingDownStatus"
            name="Cooling down"
            fill="#229DC5"
            animationDuration={0}
            stroke="#229DC5"
          />
        </AreaChart>
      </ResponsiveContainer>
      <ResponsiveContainer
        style={{ minBlockSize: '400px', userSelect: 'none' }}
        width="100%"
        height="100%"
      >
        <LineChart
          data={shownHistory}
          margin={{
            top: 5,
            right: 0,
            left: 0,
            bottom: 5,
          }}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="timestamp" tickFormatter={formatTimestamp} />
          <YAxis tickFormatter={(value) => `${value}°C`} />
          <Tooltip content={(props) => <CustomTooltip {...props} />} />
          <Legend onClick={handleLegendClick} />
          <Line
            type="monotone"
            dataKey="gasTempC"
            stroke="#FF6308"
            dot={false}
            name="Gas temp. (°C)"
            strokeOpacity={tempsShown.gasTempC ? 1 : 0}
            hide={!tempsShown.gasTempC}
            animationDuration={0}
          />
          <Line
            type="monotone"
            dataKey="waterTempC"
            stroke="#229DC5"
            dot={false}
            name="Water Temp. (°C)"
            strokeOpacity={tempsShown.waterTempC ? 1 : 0}
            hide={!tempsShown.waterTempC}
            animationDuration={0}
          />
          {refAreaLeft && refAreaRight && (
            <ReferenceArea x1={refAreaLeft} x2={refAreaRight} strokeOpacity={0.3} />
          )}
        </LineChart>
      </ResponsiveContainer>
      <div className={classes.BottomCharts}>
        <p>Fan speed</p>
        <ResponsiveContainer
          style={{ minBlockSize: '400px', userSelect: 'none' }}
          width="100%"
          height="100%"
        >
          <LineChart
            data={shownHistory}
            margin={{
              top: 5,
              right: 0,
              left: 0,
              bottom: 5,
            }}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="timestamp" tickFormatter={formatTimestamp} />
            <YAxis />
            <Tooltip content={(props) => <CustomTooltip {...props} />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="fanSpeed"
              stroke="#88C139"
              dot={false}
              name="Fan speed"
              animationDuration={0}
            />
            {refAreaLeft && refAreaRight && (
              <ReferenceArea x1={refAreaLeft} x2={refAreaRight} strokeOpacity={0.3} />
            )}
          </LineChart>
        </ResponsiveContainer>
        <p>Burning power</p>
        <ResponsiveContainer
          style={{ minBlockSize: '400px', userSelect: 'none' }}
          width="100%"
          height="100%"
        >
          <LineChart
            data={shownHistory}
            margin={{
              top: 5,
              right: 0,
              left: 0,
              bottom: 5,
            }}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="timestamp" tickFormatter={formatTimestamp} />
            <YAxis />
            <Tooltip content={(props) => <CustomTooltip {...props} />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="burningPower"
              stroke="#864EAB"
              dot={false}
              name="Burning power"
              animationDuration={0}
            />
            {refAreaLeft && refAreaRight && (
              <ReferenceArea x1={refAreaLeft} x2={refAreaRight} strokeOpacity={0.3} />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ShadowBox>
  );
};

const BarTooltip = ({ active, payload, label }: TooltipContentProps<ValueType, NameType>) => {
  if (!active || !payload || payload.length === 0) return null;

  // Find the status that has value 1
  const activeStatus = payload.find((item: unknown) => (item as { value: number }).value === 1);

  if (!activeStatus) return null;

  const color = (activeStatus as { color: string }).color;
  const name = (activeStatus as { name: string }).name;

  return (
    <div
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        color: '#333333',
      }}
    >
      <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
      <p style={{ margin: '4px 0 0 0', color }}>{name}</p>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }: TooltipContentProps<ValueType, NameType>) => {
  if (active) {
    return (
      <div
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          color: '#333333',
        }}
      >
        <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
        {payload?.map((item) => (
          <p key={item.name} style={{ margin: '4px 0 0 0', color: item.color }}>
            {item.name}: {item.value}
          </p>
        ))}
      </div>
    );
  }

  return null;
};

export default HeatingApplianceHistory;
